"use client";

import type { PostHog } from "posthog-js";

let posthogPromise: Promise<PostHog | null> | null = null;
let idleAfterLoadPromise: Promise<void> | null = null;
let isInitialized = false;

function isAnalyticsDisabled() {
  if (process.env.NEXT_PUBLIC_DISABLE_ANALYTICS === "true") return true;
  if (typeof window !== "undefined" && window.self !== window.top) return true;
  return typeof navigator !== "undefined" && navigator.webdriver;
}

type WindowWithIdleCallback = Window &
  typeof globalThis & {
    requestIdleCallback?: (
      callback: () => void,
      options?: { timeout?: number },
    ) => number;
  };

/**
 * Resolves once the page has fully loaded and the main thread has gone idle.
 * Analytics must not compete with hydration, the map, or the user's first
 * interaction for CPU time, so everything PostHog-related waits for this.
 */
const MINIMUM_IDLE_DELAY_MS = 8000;
const IDLE_TIMEOUT_MS = 2000;

export function whenIdleAfterLoad(): Promise<void> {
  if (idleAfterLoadPromise) return idleAfterLoadPromise;

  idleAfterLoadPromise = new Promise((resolve) => {
    // Outside a real browser (tests) there is no load event to wait for.
    if (typeof document === "undefined") {
      resolve();
      return;
    }
    const afterLoad = () => {
      window.setTimeout(() => {
        const idleCallback = (window as WindowWithIdleCallback)
          .requestIdleCallback;
        if (idleCallback) {
          // Bounded for the same reason the fallback below is: an idle callback
          // is not guaranteed to run in a hidden page, so opening the site in a
          // background tab would otherwise queue it until the tab is foregrounded
          // and no analytics would ever load.
          idleCallback(() => resolve(), { timeout: IDLE_TIMEOUT_MS });
        } else {
          window.setTimeout(resolve, IDLE_TIMEOUT_MS);
        }
      }, MINIMUM_IDLE_DELAY_MS);
    };
    if (document.readyState === "complete") {
      afterLoad();
    } else {
      window.addEventListener("load", afterLoad, { once: true });
    }
  });
  return idleAfterLoadPromise;
}

async function initPosthog(): Promise<PostHog | null> {
  const { default: posthog } = await import("posthog-js");
  const apiKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;

  if (!apiKey || isAnalyticsDisabled()) return null;

  if (!isInitialized) {
    posthog.init(apiKey, {
      api_host: "/ingest",
      ui_host: "https://eu.posthog.com",
      defaults: "2025-05-24",
      capture_exceptions: true, // This enables capturing exceptions using Error Tracking
      debug: process.env.NODE_ENV === "development",
      // there's no other way currently to support web vitals without cookies, as it needs a sessionid
      //cookieless_mode: "always",
      disable_persistence: true,
      person_profiles: "never",
      respect_dnt: true,
      autocapture: true,
      disable_surveys: true,
      disable_surveys_automatic_display: true,
      // Session replay's rrweb recorder is the heaviest script on the site
      // (~2.8s of mobile main-thread time on /events). Keep replay, but start
      // it below only once the browser is idle so it never competes with page
      // load. The first moments of a session are lost from replays; that is
      // the trade for not janking every visit.
      disable_session_recording: true,
      session_recording: {
        // Canvas capture is enabled server-side for this project. The only
        // canvas on the site is the MapLibre map on /events, and MapLibre
        // leaves preserveDrawingBuffer false, so every sampled frame comes
        // back fully transparent. Deleting this line costs ~1.9 s of mobile
        // main-thread time on /events and buys back nothing but those blank
        // frames. See
        // adr/0005-do-not-record-the-map-canvas-in-session-replay.md
        captureCanvas: { recordCanvas: false },
      },
    });
    isInitialized = true;
    // Best-effort, and deliberately swallowed: analytics must never take the
    // page down with it. Without the catch a throw here becomes an unhandled
    // rejection, which some environments treat as fatal. Matches how the
    // initialisation above is guarded.
    void whenIdleAfterLoad()
      .then(() => {
        posthog.startSessionRecording();
      })
      .catch(() => null);
  }

  return posthog;
}

export function loadPosthog(): Promise<PostHog | null> {
  if (typeof window === "undefined") return Promise.resolve(null);
  if (isAnalyticsDisabled()) return Promise.resolve(null);

  if (!posthogPromise) {
    posthogPromise = initPosthog().catch(() => null);
  }

  return posthogPromise;
}
